/**
 * Areas — the PARA containers with no end date (SPEC.md §4), self-contained the way
 * `RecurringCard`/`NotesSection` are: its own fetch, its own mutations, a `refreshKey` to
 * catch changes made elsewhere and an `onChange` to report its own. That is what lets it
 * mount on any page, not just the one it was originally built for.
 *
 * §8.5 says thresholds are per-row "because some areas are legitimately quiet" — the reason
 * `stale_after_days` is editable here at all, rather than fixed by the seed.
 */

import { useCallback, useRef, useState } from "react";
import { ApiError, api } from "../api";
import { queueAreaCreate } from "../taskQueue";
import { useAreasAndProjects } from "../areasProjects";
import { useFireworks } from "./fireworksContext";
import { basicTaskActions } from "../taskActions";
import { todayLocalDate } from "./recurrence";
import { useRowReorder } from "./useRowReorder";
import { EyeToggle } from "./EyeToggle";
import { AddRow, StructureRow } from "./StructureRow";

export function AreasCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed — a project's area, a task moving in or
   * out of a drawer here — so this card's own list and its drawers' cross-references stay
   * current without a full reload. */
  refreshKey?: number;
  /** Called after this card changes anything, so siblings (Projects, wherever it lives)
   * relying on the same data know to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects, error: loadError, reload, loaded } = useAreasAndProjects(refreshKey);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const burst = useFireworks();
  const actions = basicTaskActions(burst);
  const today = todayLocalDate();

  // What was already archived *before* this session started, captured once on the first
  // successful load — never recomputed on a later `reload()`. An area archived during this
  // session isn't in here, so it stays visible, crossed out, through any number of reloads;
  // only an actual page refresh (a fresh mount, a fresh snapshot) folds it away (gitea #49,
  // D205), same pattern `ProjectsCard` already uses (D204).
  const priorArchived = useRef<Set<string> | null>(null);
  if (loaded && priorArchived.current === null) {
    priorArchived.current = new Set(
      areas.filter((a) => a.status === "archived").map((a) => a.id),
    );
  }
  const visibleAreas = areas.filter((area) => {
    if (area.status !== "archived") return true;
    return showArchived || !(priorArchived.current?.has(area.id) ?? false);
  });

  /**
   * `whenOffline`, where given, is what to do instead of failing when the server cannot be
   * reached — queue it (§8.2). Either way the reload below runs, because the mirror has
   * already been patched and re-reading is how the change reaches the screen (D166).
   */
  const act = useCallback(
    async (fn: () => Promise<unknown>, whenOffline?: () => Promise<unknown>) => {
      try {
        await fn();
      } catch (err) {
        if (whenOffline && err instanceof ApiError && err.isOffline) {
          await whenOffline();
        } else {
          setError(err instanceof Error ? err.message : String(err));
          return;
        }
      }
      await reload();
      await onChange?.();
    },
    [reload, onChange],
  );

  // No API call of its own — a drawer already made one — just the same reload/onChange
  // tail `act` gives every other mutation here, so a task ticked off inside a row's drawer
  // updates that row's own `· N` count and tells siblings relying on the same data too.
  const notifyDrawerChange = useCallback(async () => {
    await reload();
    await onChange?.();
  }, [reload, onChange]);

  /**
   * Move an item to an insertion point, then persist the whole order in one call.
   *
   * `insertAt` is a gap between rows, not a row: 0 is above the first, `length` is below
   * the last. That is what makes the bottom of the list reachable — addressing rows instead
   * meant there was no way to say "after the last one", so the final position could only be
   * reached by moving something else out of it.
   *
   * Removing the dragged item first shifts every later gap down by one, hence the
   * adjustment.
   */
  const reorder = useCallback(
    (from: number, insertAt: number) => {
      const target = from < insertAt ? insertAt - 1 : insertAt;
      if (from === target) return;
      const next = [...visibleAreas];
      const [moved] = next.splice(from, 1);
      next.splice(target, 0, moved);
      // A partial id list is fine — `reorder_areas` (routers/meta.py) keeps anything not
      // named in its existing relative order after the ones that are, so a folded-away
      // archived area just stays where it was rather than getting yanked to the end.
      void act(() => api.reorderAreas(next.map((a) => a.id)));
    },
    [visibleAreas, act],
  );

  const areaDrag = useRowReorder(reorder, visibleAreas.length);

  /** What archiving this area will fold out of view — the confirmation's own enumeration
   * (gitea #49, D206), so "keeps everything, stops it appearing anywhere" isn't just a
   * promise in the tooltip. */
  function contentsOf(area: (typeof areas)[number]): string {
    const parts: string[] = [];
    if (area.open_tasks) parts.push(`${area.open_tasks} open task${area.open_tasks === 1 ? "" : "s"}`);
    if (area.active_projects) {
      parts.push(`${area.active_projects} active project${area.active_projects === 1 ? "" : "s"}`);
    }
    if (area.notes_count) parts.push(`${area.notes_count} note${area.notes_count === 1 ? "" : "s"}`);
    return parts.join(", ");
  }

  return (
    <section className="panel">
      <header>
        <h2>Areas</h2>
        <span className="row">
          <span className="muted small">Ongoing spheres of responsibility. No end date.</span>
          <EyeToggle
            open={showArchived}
            onToggle={() => setShowArchived((v) => !v)}
            label="archived areas"
          />
        </span>
      </header>

      {(error ?? loadError) && (
        <p className="error" style={{ margin: "0 0 0.6rem" }}>
          {error ?? loadError}
        </p>
      )}

      <p className="muted small">
        <strong>Quiet after</strong> is how long an area may go with nothing <em>finished</em>{" "}
        in it before it shows up under Gone quiet. Only completing something resets that
        clock - starring, editing and snoozing deliberately do not. Drag a row by its handle
        to reorder.
      </p>

      {/*
        Roles are spelled out because the narrow-screen layout sets `display: block` on
        these elements, and that drops their implicit table roles in Chrome and Safari -
        taking the row and cell structure, and the sort state, with it.
      */}
      <div className="structure-wrap">
        <table className="tokens structure" role="table">
          <thead role="rowgroup">
            <tr role="row">
              <th role="columnheader">Name</th>
              <th role="columnheader" className="unsorted">
                Quiet after
              </th>
              <th role="columnheader" className="spacer" />
              <th role="columnheader" className="spacer" />
            </tr>
          </thead>
          <tbody role="rowgroup">
            {visibleAreas.map((area, index) => (
              <StructureRow
                key={area.id}
                id={area.id}
                kind="area"
                index={index}
                draggable
                gripProps={areaDrag.gripProps}
                rowProps={areaDrag.rowProps}
                today={today}
                allAreas={areas}
                allProjects={projects}
                actions={actions}
                drawerRefreshKey={refreshKey}
                onDrawerChange={notifyDrawerChange}
                onDelete={() => act(() => api.deleteArea(area.id))}
                name={area.name}
                threshold={area.stale_after_days}
                taskCount={area.open_tasks}
                archived={area.status !== "active"}
                statusLabel={area.status === "active" ? "Archive" : "Restore"}
                statusHint={
                  area.status === "active"
                    ? "Archive: keeps everything, stops it appearing anywhere you file or review (§4.1)"
                    : "Bring it back into circulation"
                }
                onSave={(patch) => act(() => api.patchArea(area.id, patch))}
                onStatus={() => {
                  if (area.status === "active") {
                    const contents = contentsOf(area);
                    const detail = contents
                      ? `\n\nIt holds ${contents}, all of which will be hidden from your ` +
                        "other views until you restore it."
                      : "";
                    if (!window.confirm(`Archive "${area.name}"?${detail}`)) return;
                  }
                  void act(() =>
                    api.patchArea(area.id, {
                      status: area.status === "active" ? "archived" : "active",
                    }),
                  );
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      <AddRow
        label="area"
        onAdd={(name, threshold) =>
          act(
            () => api.createArea({ name, stale_after_days: threshold }),
            () => queueAreaCreate(name),
          )
        }
      />
    </section>
  );
}
